import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Utensils, Clock, Star, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import type { NotificationItem } from '@/hooks/useNotifications';

interface CheckinNotificationItemProps {
  notification: NotificationItem;
  isUnread: boolean;
  onClose: () => void;
}

export function CheckinNotificationItem({ notification, isUnread, onClose }: CheckinNotificationItemProps) {
  const navigate = useNavigate();
  const { data } = notification;

  const handleClick = () => {
    if (data.burger_id) {
      navigate(`/burgers/${data.burger_id}`);
    }
    onClose();
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer",
        isUnread && "bg-primary/5 border-l-2 border-primary"
      )}
      onClick={handleClick}
    >
      {data.checker_avatar_url ? (
        <Avatar className="h-12 w-12 flex-shrink-0">
          <AvatarImage src={data.checker_avatar_url} />
          <AvatarFallback className="bg-primary/10 text-primary">
            {getInitials(data.checker_name)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="h-12 w-12 rounded-full flex items-center justify-center bg-orange-100 dark:bg-orange-900/20">
          <Utensils className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
      )}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 mb-1">
          <div className="flex-1">
            <p className={cn(
              "text-foreground text-sm",
              isUnread ? "font-semibold" : "font-medium"
            )}>
              {data.checker_name || 'Someone'} checked in to{' '}
              <span className="text-primary">{data.burger_name || 'your burger'}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
          {data.rating && (
            <div className="flex items-center gap-1">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              <span>{data.rating}/10</span>
            </div>
          )}
          {data.place_name && (
            <div className="flex items-center gap-1 truncate">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{data.place_name.split(',')[0]}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
          <Clock className="h-3 w-3" />
          <span>
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
      
      {data.photo_url && (
        <img 
          src={data.photo_url} 
          alt="Checkin photo" 
          className="h-14 w-14 rounded-lg object-cover flex-shrink-0"
        />
      )}
    </div>
  );
}
